/**
 * MentionReviewSection Types
 *
 * TypeScript interfaces for mention review components
 */
import type { MentionReviewDto } from '@/domain/entities/MentionReview';

/**
 * Props for MentionReviewSection component
 */
export interface MentionReviewSectionProps {
  /**
   * Additional CSS class name
   */
  className?: string;
}

/**
 * Props for MentionReviewFilters component
 */
export interface MentionReviewFiltersProps {
  /**
   * Current review status filter
   */
  reviewStatus: string | null;

  /**
   * Callback when review status changes
   */
  onReviewStatusChange: (status: string | null) => void;

  /**
   * Current minimum confidence filter
   */
  minConfidence: number | null;

  /**
   * Callback when minimum confidence changes
   */
  onMinConfidenceChange: (value: number | null) => void;

  /**
   * Current maximum confidence filter
   */
  maxConfidence: number | null;

  /**
   * Callback when maximum confidence changes
   */
  onMaxConfidenceChange: (value: number | null) => void;

  /**
   * Callback when confidence range changes (both min and max)
   * Use this for slider to avoid URL parameter conflicts
   */
  onConfidenceRangeChange: (min: number, max: number) => void;

  /**
   * Current subreddit filter
   */
  subredditName: string | null;

  /**
   * Callback when subreddit changes
   */
  onSubredditNameChange: (subreddit: string | null) => void;

  /**
   * Current region IDs filter (Sprint 013)
   */
  regionIds: string[];

  /**
   * Callback when region IDs change (Sprint 013)
   */
  onRegionIdsChange: (regionIds: string[]) => void;

  /**
   * Current business name filter (partial match)
   * Replaces businessId autocomplete - now filters by name text
   */
  businessNameFilter: string | null;

  /**
   * Callback when business name filter changes
   */
  onBusinessNameFilterChange: (filter: string | null) => void;

  /**
   * Current algorithm version filter (Sprint 038)
   */
  algorithmVersion: string | null;

  /**
   * Callback when algorithm version filter changes (Sprint 038)
   */
  onAlgorithmVersionChange: (version: string | null) => void;

  /**
   * List of available algorithm versions for dropdown (Sprint 038)
   */
  algorithmVersionOptions: string[];

  /**
   * Whether algorithm versions are loading (Sprint 038)
   */
  algorithmVersionsLoading: boolean;

  /**
   * Current sort by field
   */
  sortBy: string;

  /**
   * Callback when sort by field changes
   */
  onSortByChange: (sortBy: string) => void;

  /**
   * Current sort direction
   */
  sortDirection: string;

  /**
   * Callback when sort direction changes
   */
  onSortDirectionChange: (direction: string) => void;

  /**
   * Callback to clear all filters
   */
  onClearFilters: () => void;

  /**
   * Whether data is currently being fetched (for loading indicators)
   */
  isFetching?: boolean;
}

/**
 * Props for MentionReviewList component
 * Sprint 013: Updated signatures for inline approve/reject, removed onMentionClick (no card navigation)
 */
export interface MentionReviewListProps {
  /**
   * Mention data to display
   */
  mentions: MentionReviewDto[];

  /**
   * Whether data is loading
   */
  isLoading: boolean;

  /**
   * Error if fetch failed
   */
  error: Error | null;

  /**
   * Current page number
   */
  page: number;

  /**
   * Total pages
   */
  totalPages: number;

  /**
   * Callback when page changes
   */
  onPageChange: (page: number) => void;

  /**
   * Callback when approve action is confirmed
   * Sprint 013: with optional notes
   * Sprint 014 Phase 6: with required businessIds array (SESSION_116)
   */
  onApprove: (mention: MentionReviewDto, businessIds: string[], adminNotes?: string, campaignType?: string) => Promise<void>;

  /**
   * Callback when reject action is confirmed (Sprint 013: with reason and notes)
   */
  onReject: (mention: MentionReviewDto, rejectionReason: string, adminNotes?: string) => Promise<void>;

  /**
   * Whether actions are disabled (mutations in progress)
   */
  actionsDisabled: boolean;
}

/**
 * Props for MentionReviewCard component
 * Sprint 013: Updated signatures for inline approve/reject, removed onClick (no card navigation)
 * Sprint 014 Phase 6: Multi-business approval support (SESSION_116)
 */
export interface MentionReviewCardProps {
  /**
   * Mention data
   */
  mention: MentionReviewDto;

  /**
   * Callback when approve action is confirmed
   * Sprint 013: with optional notes
   * Sprint 014 Phase 6: with required businessIds array (SESSION_116)
   */
  onApprove: (mention: MentionReviewDto, businessIds: string[], adminNotes?: string, campaignType?: string) => Promise<void>;

  /**
   * Callback when reject action is confirmed (Sprint 013: with reason and notes)
   */
  onReject: (mention: MentionReviewDto, rejectionReason: string, adminNotes?: string) => Promise<void>;

  /**
   * Whether actions are disabled
   */
  actionsDisabled: boolean;
}

/**
 * Props for MentionReviewModal component
 * Sprint 014 Phase 6: Multi-business approval support (SESSION_116)
 */
export interface MentionReviewModalProps {
  /**
   * Whether modal is open
   */
  opened: boolean;

  /**
   * Callback when modal should close
   */
  onClose: () => void;

  /**
   * Mention data to display
   */
  mention: MentionReviewDto | null;

  /**
   * Callback when approve action is clicked (Sprint 014 Phase 6: requires businessIds)
   */
  onApprove: (mention: MentionReviewDto, businessIds: string[], adminNotes?: string, campaignType?: string) => void;

  /**
   * Callback when reject action is clicked
   */
  onReject: (mention: MentionReviewDto, rejectionReason: string, adminNotes?: string) => void;

  /**
   * Whether actions are disabled
   */
  actionsDisabled: boolean;
}

/**
 * Props for ActionButtons component
 */
export interface ActionButtonsProps {
  /**
   * Mention data
   */
  mention: MentionReviewDto;

  /**
   * Callback when approve is clicked
   */
  onApprove: (mention: MentionReviewDto) => void;

  /**
   * Callback when reject is clicked
   */
  onReject: (mention: MentionReviewDto) => void;

  /**
   * Whether buttons are disabled
   */
  disabled: boolean;

  /**
   * Button size
   */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';

  /**
   * Compact mode (smaller spacing)
   */
  compact?: boolean;
}

/**
 * Confidence level thresholds
 */
export const CONFIDENCE_LEVELS = {
  LOW: { min: 50, max: 69, label: 'Low', color: 'orange' },
  MEDIUM: { min: 70, max: 94, label: 'Medium', color: 'yellow' },
  HIGH: { min: 95, max: 100, label: 'High', color: 'green' }
} as const;

/**
 * Get confidence level info for a score
 */
export function getConfidenceLevel(score: number): {
  label: string;
  color: string;
} {
  if (score >= CONFIDENCE_LEVELS.HIGH.min) {
    return { label: CONFIDENCE_LEVELS.HIGH.label, color: CONFIDENCE_LEVELS.HIGH.color };
  }
  if (score >= CONFIDENCE_LEVELS.MEDIUM.min) {
    return { label: CONFIDENCE_LEVELS.MEDIUM.label, color: CONFIDENCE_LEVELS.MEDIUM.color };
  }
  return { label: CONFIDENCE_LEVELS.LOW.label, color: CONFIDENCE_LEVELS.LOW.color };
}
